import React, { useState, useEffect } from 'react'
import { getSessionHeaders } from '../../lib/serverSession'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface Automation {
  id: number
  name: string
  description: string
  automation_type: string
  trigger_condition: { from_status?: string; days_inactive?: number; priority?: string; no_response_hours?: number }
  action: { to_status?: string; notify?: boolean; escalate_to?: string }
  is_active: boolean
  last_run_at: string | null
  last_run_count: number
  total_run_count: number
}

const TicketAutomations: React.FC = () => {
  const [automations, setAutomations] = useState<Automation[]>([])
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)
  const [runResults, setRunResults] = useState<Array<{ automation: string; affected?: number; error?: string }> | null>(null)
  const [error, setError] = useState('')

  useEffect(() => { loadAutomations() }, [])

  const loadAutomations = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/support`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'load_automations' })
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error)
      setAutomations(json.data || [])
    } catch (err: any) { setError(err.message) }
    setLoading(false)
  }

  const toggleAutomation = async (id: number, is_active: boolean) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/support`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'update_automation', data: { id, is_active } })
      })
      if (!res.ok) { const json = await res.json(); throw new Error(json.error) }
      loadAutomations()
    } catch (err: any) { setError(err.message) }
  }

  const runAllAutomations = async () => {
    if (!confirm('Run all active automations now? This will process tickets matching each rule.')) return
    setRunning(true); setRunResults(null); setError('')
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/support`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'run_automations' })
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error)
      setRunResults(json.data || [])
      loadAutomations()
    } catch (err: any) { setError(err.message) }
    setRunning(false)
  }

  const typeIcon = (type: string) => type === 'auto_close' ? '🔒' : type === 'auto_escalate' ? '⬆️' : '⚙️'

  const formatDate = (d: string | null) => d ? new Date(d).toLocaleString() : 'Never'

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Ticket Automations</h3>
          <p className="text-sm text-gray-500">{automations.filter(a => a.is_active).length} active rules</p>
        </div>
        <button onClick={runAllAutomations} disabled={running}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 text-sm font-medium flex items-center gap-2">
          {running ? <><span className="animate-spin">⏳</span> Running...</> : '▶️ Run All Automations'}
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}
        <button onClick={() => setError('')} className="ml-2 font-bold">×</button></div>}

      {runResults && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-green-800 mb-2">Automation Results</h4>
          {runResults.map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              {r.error ? <span className="text-red-600">❌ {r.automation}: {r.error}</span>
                : <span className="text-green-700">✅ {r.automation}: {r.affected} ticket{r.affected !== 1 ? 's' : ''} affected</span>}
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div></div>
      ) : (
        <div className="space-y-3">
          {automations.map(auto => (
            <div key={auto.id} className={`bg-white border rounded-lg p-4 ${!auto.is_active ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-xl">{typeIcon(auto.automation_type)}</span>
                  <div>
                    <h4 className="font-medium text-gray-900">{auto.name}</h4>
                    <p className="text-sm text-gray-500">{auto.description}</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={auto.is_active} onChange={() => toggleAutomation(auto.id, !auto.is_active)} className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>
              <div className="mt-3 flex items-center gap-4 text-sm text-white">
                <span>Type: <span className="text-gray-600">{auto.automation_type.replace('_', ' ')}</span></span>
                {auto.trigger_condition?.days_inactive && <span>After: <span className="text-gray-600">{auto.trigger_condition.days_inactive} days</span></span>}
                {auto.trigger_condition?.from_status && <span>From: <span className="text-gray-600">{auto.trigger_condition.from_status}</span></span>}
                <span>Last run: <span className="text-gray-600">{formatDate(auto.last_run_at)}</span></span>
                <span>Last affected: <span className="text-gray-600">{auto.last_run_count}</span></span>
                <span>Total affected: <span className="text-gray-600">{auto.total_run_count}</span></span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default TicketAutomations

